import { useCallback } from 'react'

import { getBuildProgressConfig, resetBuildProgressConfig, setBuildProgressConfig } from '@/atoms'
import { useBuildProgressModal } from '@/components/modals/BuildProgressModal'
import { buildRoutes } from '@/features/domain/route'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import { useTexts } from '../useTexts'
import { useController, type BalanceBy, type ModalData } from './useController'

function buildBalancingStrategy(
  balanceBy: Exclude<BalanceBy, ''>,
  loadType: string,
): uui.domain.actions.rpc.route.BalancingStrategy {
  switch (balanceBy) {
    case 'orders':
      return { traveledDistance: 0, workingTime: 0, load: 0, ordersCount: 100 }
    case 'workingTime':
      return { traveledDistance: 0, workingTime: 100, load: 0, ordersCount: 0 }
    case 'distance':
      return { traveledDistance: 100, workingTime: 0, load: 0, ordersCount: 0 }
    case 'loadType':
      return {
        traveledDistance: 0,
        workingTime: 0,
        load: 100,
        loadName: loadType,
        ordersCount: 0,
      }
  }
}

export function mapControllerDataToPayload(
  data: ModalData,
): uui.domain.actions.rpc.route.BuildRoutesPayload {
  const payload: uui.domain.actions.rpc.route.BuildRoutesPayload = {
    minimizeVehicles: data.driverUsage === 'minimizeDrivers',
    skipMinRequirements: false,
  }

  if (data.strategy === 'balancedPerformance' && data.balanceBy !== '') {
    payload.balancing = {
      strategy: buildBalancingStrategy(data.balanceBy, data.loadType),
    }
    payload.skipMinRequirements = data.ignoreConstraints
  }

  return payload
}

function buildStrategyLabel(data: ModalData, texts: ReturnType<typeof useTexts>): string {
  const driverPart =
    data.driverUsage === 'minimizeDrivers' ? texts.configMinDrivers : texts.configAllDrivers

  if (data.strategy === 'fleetSavings') {
    return `${texts.fleetSavings}: ${driverPart}`
  }

  const balanceLabel = (() => {
    switch (data.balanceBy) {
      case 'orders':
        return texts.configBalanceOrderDistribution
      case 'workingTime':
        return texts.configBalanceWorkingTime
      case 'distance':
        return texts.configBalanceDistance
      case 'loadType':
        return texts.configBalanceLoadDistribution(data.loadType || '—')
      default:
        return ''
    }
  })()

  return `${texts.balancedPerformance}: ${balanceLabel} (${driverPart})`
}

function isInvalid(data: ModalData): boolean {
  if (data.strategy !== 'balancedPerformance') return false
  if (data.balanceBy === '') return true
  if (data.balanceBy === 'loadType' && !data.loadType) return true
  return false
}

export function useControllerActions() {
  const texts = useTexts()
  const toast = useNotification()
  const dispatch = useAppDispatch()
  const { data, updateData } = useController()
  const { show: showBuildProgressModal, close: closeBuildProgressModal } = useBuildProgressModal()

  const handleStart = useCallback(() => {
    if (isInvalid(data)) {
      updateData({ submitAttempted: true })
      return
    }

    const payload = mapControllerDataToPayload(data)

    // Publish the config so the Build Progress modal can render it.
    setBuildProgressConfig({ strategyLabel: buildStrategyLabel(data, texts) })

    // Switch to the Build Progress modal BEFORE dispatching so it's mounted in
    // time to catch the first `buildProgress` notification. `show()` swaps the
    // active modalId, which implicitly hides this modal — do NOT call close()
    // here (it would reset modalState entirely and unmount Build Progress too).
    showBuildProgressModal()

    // Fire and forget — on success the build emits `buildProgress: 'completed'`
    // and the Build Progress modal shows its completion screen. On failure the
    // modal can't recover on its own (it has no close button while in progress),
    // so close it, clear the published config, and surface a toast. Guarded on
    // the config still being set so a user-initiated discard doesn't double-handle.
    dispatch(buildRoutes(payload)).then(result => {
      if (!buildRoutes.fulfilled.match(result) && getBuildProgressConfig()) {
        resetBuildProgressConfig()
        closeBuildProgressModal()
        // Surface the real backend error message (RejectError.message) when
        // present, falling back to the generic copy — matches the pattern used
        // across the Routes actions.
        toast.error(result.payload?.message ?? texts.toastError)
      }
    })
  }, [data, dispatch, showBuildProgressModal, closeBuildProgressModal, texts, toast, updateData])

  // isLoading retained for the Footer's button props compat; the modal swaps
  // immediately so it's effectively always false.
  return { handleStart, isLoading: false }
}
